import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchReadiness, geoCheck } from "../../api/attendance";
import { dutyGeoCheck } from "../../api/duties";
import { useGeolocation } from "../../hooks/useGeolocation";
import { useLivenessSession } from "./useLivenessSession";
import { useCheckSubmit } from "./useCheckSubmit";
import { useGreeting } from "./useGreeting";
import { gateOf } from "./captureGates";
import { useQrExpiry } from "./useQrExpiry";

export const useCheckFlow = ({ branchId, dutyId, onDone }) => {
  const { t } = useTranslation();
  const { locate, loading: locating, error: geoError } = useGeolocation();
  const liveness = useLivenessSession();
  const [geo, setGeo] = useState(null);
  const [open, setOpen] = useState(false);
  const [action, setAction] = useState("in");
  const [phase, setPhase] = useState("idle");
  const [message, setMessage] = useState("");
  const [similarity, setSimilarity] = useState(null);
  const [readiness, setReadiness] = useState(null);
  const [checking, setChecking] = useState(false);
  const [qrCode, setQrCode] = useState("");
  const greeting = useGreeting();
  const ui = { setPhase, setMessage, setSimilarity, setOpen };
  const { submit, submitLiveness, fail } = useCheckSubmit({
    branchId, dutyId, action, locate, liveness, onDone, ui, qrCode, greeting,
  });

  // Re-checks location, time, face and credits so the capture button only unlocks when all pass.
  const verifyReadiness = useCallback(
    async (nextAction, silent = false, code = qrCode) => {
      if (!silent) setChecking(true);
      try {
        const coords = await locate();
        const next = await fetchReadiness(branchId, coords.latitude, coords.longitude, nextAction, dutyId, code);
        setReadiness((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
      } catch {
        if (!silent) setReadiness(null);
      } finally {
        if (!silent) setChecking(false);
      }
    },
    [branchId, dutyId, locate, qrCode]
  );

  const applyQrCode = async (raw) => {
    const code = (raw || "").trim().toUpperCase().replace("HELCOO:Z:", "");
    setQrCode(code);
    await verifyReadiness(action, false, code);
  };
  useQrExpiry({ open, qrCode, onExpire: () => { setQrCode(""); verifyReadiness(action, true, ""); } });
  const start = async (nextAction) => {
    if (!branchId) return;
    setAction(nextAction);
    setPhase("idle");
    setMessage("");
    setSimilarity(null);
    setReadiness(null);
    setQrCode("");
    setOpen(true);
    greeting.prepare();
    try {
      const coords = await locate();
      setGeo(
        dutyId
          ? await dutyGeoCheck(dutyId, coords.latitude, coords.longitude)
          : await geoCheck(branchId, coords.latitude, coords.longitude)
      );
      await verifyReadiness(nextAction);
      await liveness.newSession();
    } catch {
      setPhase("error");
      setMessage(t("check.gpsDenied"));
    }
  };

  const retry = async () => {
    setPhase("idle");
    setMessage("");
    setSimilarity(null);
    if (!liveness.sessionId) await liveness.newSession();
  };

  useEffect(() => {
    if (!open || phase === "verifying" || phase === "success") return undefined;
    const timer = setInterval(() => verifyReadiness(action, true), 60000);
    return () => clearInterval(timer);
  }, [open, phase, action, verifyReadiness]);

  return {
    geo, geoError, locating, open, setOpen, action, phase, message, similarity,
    readiness, checking, start, submit, submitLiveness, liveness, fail, retry,
    qrCode, applyQrCode,
    // الكاميرا لا تعمل إلا بعد اجتياز كل البوابات — لا بث ولا إضاءة قبل ذلك
    cameraAllowed:
      open && !liveness.enabled && Boolean(readiness) && !checking && gateOf(readiness, true).kind === null,
  };
};
